import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { AskForRole, NO_ROLE_LEAD } from "@/components/toolbox/setup/ask-for-role";
import { adoPatStepOk, ConnectAdoStep, type AdoPatPassed } from "@/components/toolbox/setup/connect-ado-step";
import { SetupStepList } from "@/components/toolbox/setup/setup-step-list";
import { useAdoPatStatus, useMe } from "@/hooks/queries/use-me";
import { hasNoRole, useMeFollowsToolboxAccess, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { usePageTitle } from "@/hooks/use-page-title";
import { cn } from "@/lib/utils";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import {
  ADO_SETUP_STEP,
  isFixItSetupStep,
  isQueueReturnPath,
  patPageName,
  QUEUE_PAGE_NAME,
  safeToolboxReturnPath,
} from "@/lib/toolbox/page-names";
import {
  accountStepsOnly,
  allStepsOk,
  needsComputerSteps,
  useSetupReport,
  type SetupRows,
} from "@/lib/toolbox/setup";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";

/** The page's lead line (frame 1b). */
export const SETUP_LEAD = "Five steps, in order. Each one has a button that does it for you.";

/** The line beside Continue (frame 1b). A full sentence, so 13 px (driver decision 2026-10-21). */
export const SETUP_FOOTER_NOTE = "Continue opens when all five steps pass. You can leave this page; setup keeps going.";

/** The lead for someone whose roles only read (a Viewer): no Claude Code, Git or project code (PKG-PM-E walk E-W6). */
export const SETUP_LEAD_READ_ONLY =
  "Two steps, in order. Your role only reads, so you need no Claude Code, Git or project code.";

/** The line beside Continue for someone whose roles only read. */
export const SETUP_FOOTER_NOTE_READ_ONLY = "Continue opens when both steps pass. You can leave this page; setup keeps going.";

/** The blue line on a PAT redirect (frame 1c). */
export function patRedirectLine(page: string): string {
  return `${page} needs your Azure DevOps connection. Finish this step and you'll go straight back to it.`;
}

/** The blue line when a paused queue's Fix it opened this page (epic PI-SPEC-SW-E290). */
export const QUEUE_RETURN_LINE = "The Queue waits for this step. When it passes, you'll go straight back and the queue starts again.";

/** The small notice when a background re-check failed but the last result is still shown. */
export const SETUP_RECHECK_FAILED = "Couldn't check your computer just now. This is the last result; it checks again on its own.";

/**
 * The page's rows: the app's report with step 2 replaced by what SpecTree
 * knows (a saved PAT that is not past its expiry, `adoPatStepOk`). The PAT is
 * entered and checked on this page, so SpecTree, not the app, is the source
 * for that step. Pure; exported for tests.
 */
export function withAzureDevOps(rows: SetupRows, patOk: boolean): SetupRows {
  return {
    steps: rows.steps.map((step) =>
      step.id === "azure-devops" ? { id: "azure-devops", status: patOk ? "ok" : "todo" } : step,
    ),
  };
}

function RedirectInfo({ text }: { text: string }) {
  return (
    <div
      className={cn(
        "mt-3.5 flex items-center gap-2.5 rounded-[10px] border border-[rgba(35,164,255,.28)] bg-[rgba(35,164,255,.10)] px-3.5 py-2.5 text-[#BFE3FF]",
        TOOLBOX_TEXT.content,
      )}
      role="status"
      data-testid="setup-redirect-info"
    >
      <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">{text}</span>
    </div>
  );
}

/**
 * `/toolbox/setup` (epic PI-SPEC-SW-E288, feature PI-SPEC-SW-1466; frames 1b,
 * 1c and 1d; 03-evaluation.md section 8.6 Q5, Q8, Q10): one ordered list of
 * five steps, SpecTree → Azure DevOps → Claude Code → Git → Project code. A
 * later step "Waits for step N"; each failing row has one button that does
 * the fix and never a command. Azure DevOps is connected and tested here, not
 * on SpecTree Settings. Someone whose roles only read (a Viewer) sees just
 * SpecTree and Azure DevOps (`needsComputerSteps`, PKG-PM-E walk E-W6).
 * Continue opens when all shown steps pass; someone with no
 * Toolbox role sees "Ask for a role" instead, and the page re-checks every
 * minute.
 *
 * A Toolbox page that needs the PAT sends people here with
 * `?step=azure-devops&from=…&page=…`; the page names it in a blue line and
 * returns to `from` after a pass (only for Toolbox paths, see
 * `safeToolboxReturnPath`). In a plain browser the Azure DevOps step still
 * works, above the "Open in SpecTree Toolbox" callout.
 *
 * A paused queue's "Fix it" (epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1479)
 * sends people here with `?step=azure-devops|claude-code|git&from=/grooming/
 * queue?fixed=1&page=Queue`: the page opens and scrolls to that step, says
 * the Queue waits for it (never the Azure DevOps line: the Queue needs no
 * PAT), offers "Back to Queue", and goes back by itself once the step passes
 * during this visit. The Queue then asks the app to start again.
 *
 * "Check again" re-reads the computer's steps, the Toolbox access and the PAT
 * status at once (rows without a button otherwise wait for the minute poll).
 * A background re-check that fails keeps the last list on screen with a small
 * notice; only a first read that fails shows the update message.
 */
export function ToolboxSetupPage() {
  usePageTitle("SpecTree Toolbox setup");
  const bridge = useToolboxBridge();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const access = useToolboxAccess();
  const { data: me } = useMe();
  const adoPat = useAdoPatStatus();
  const setup = useSetupReport(bridge);
  useMeFollowsToolboxAccess();
  const [noCodeRead, setNoCodeRead] = useState(false);

  const returnTo = safeToolboxReturnPath(params.get("from"));
  const returnPathname = returnTo ? (returnTo.split(/[?#]/, 1)[0] ?? "") : "";
  const redirectedFrom = returnTo ? patPageName(returnPathname) : null;
  const queueReturn = returnTo !== null && isQueueReturnPath(returnPathname);
  const returnName = redirectedFrom ?? (queueReturn ? QUEUE_PAGE_NAME : null);
  const stepParam = params.get("step");
  const askedStep = isFixItSetupStep(stepParam) ? stepParam : null;
  const adoStepAsked = askedStep === ADO_SETUP_STEP;
  const noRole = hasNoRole(access.data);
  const computerSteps = needsComputerSteps(access.data);

  // Scroll to the asked step once per arrival with ?step=…, not on every re-read of the report.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!askedStep || scrolledFor.current === location.key) return;
    const row = document.querySelector(`[data-testid="setup-step-${askedStep}"]`);
    if (!row) return;
    row.scrollIntoView({ block: "center" });
    scrolledFor.current = location.key;
  }, [askedStep, location.key, setup.data]);

  const onPassed = (result: AdoPatPassed) => {
    setNoCodeRead(result.codeRead === false);
    if (bridge) void setup.refetch();
    // A repository setter whose token cannot read code stays here to read the warning.
    if (returnTo && result.codeRead !== false) void navigate(returnTo, { replace: true });
  };

  const checking = setup.isFetching || access.isFetching || adoPat.isFetching;
  const checkAgain = () => {
    if (bridge) void setup.refetch();
    void access.refetch();
    void adoPat.refetch();
  };

  const azureDevOps = <ConnectAdoStep initiallyOpen={adoStepAsked} onPassed={onPassed} />;
  const allRows = setup.data ? withAzureDevOps(setup.data.rows, adoPatStepOk(adoPat.data, new Date())) : null;
  const rows = allRows && !computerSteps ? accountStepsOnly(allRows) : allRows;
  const ready = rows ? allStepsOk(rows) : false;

  // A queue Fix it on Claude Code or Git: go back once that step passes during this visit (it was seen not ok first).
  const askedStatus = askedStep && askedStep !== ADO_SETUP_STEP ? rows?.steps.find((step) => step.id === askedStep)?.status : undefined;
  const sawAskedStepTodo = useRef(false);
  useEffect(() => {
    if (!queueReturn || !returnTo || askedStatus === undefined) return;
    if (askedStatus !== "ok") {
      sawAskedStepTodo.current = true;
      return;
    }
    if (sawAskedStepTodo.current) void navigate(returnTo, { replace: true });
  }, [askedStatus, navigate, queueReturn, returnTo]);

  const checkAgainButton = (
    <Button variant="outline" size="sm" className="h-9 md:h-9" onClick={checkAgain} disabled={checking}>
      <RefreshCw className={cn(checking && "animate-spin")} aria-hidden="true" />
      Check again
    </Button>
  );

  return (
    <div className="container mx-auto flex justify-center px-4 pb-12">
      <div className="mt-[34px] flex w-full max-w-[760px] flex-col">
        <h1 className={TOOLBOX_PAGE_TITLE}>Set up SpecTree Toolbox</h1>
        <p className="mt-1.5 text-sm text-muted-foreground" data-testid="setup-lead">
          {noRole ? NO_ROLE_LEAD : computerSteps ? SETUP_LEAD : SETUP_LEAD_READ_ONLY}
        </p>
        {redirectedFrom ? <RedirectInfo text={patRedirectLine(redirectedFrom)} /> : null}
        {queueReturn ? <RedirectInfo text={QUEUE_RETURN_LINE} /> : null}

        <div className="mt-5">
          {!bridge ? (
            <ul aria-label="Setup steps" className="overflow-hidden rounded-xl border border-[#1F1F23] bg-[#0E0E10]">
              {azureDevOps}
            </ul>
          ) : setup.isPending ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="status">
              Checking your computer…
            </p>
          ) : !rows ? (
            // Only with nothing to show: a failed background re-check keeps the last list (and the PAT form) on screen.
            <div
              className={cn(
                "flex items-start gap-2 rounded-[10px] border border-amber-500/25 bg-amber-500/[0.08] p-4",
                TOOLBOX_TEXT.content,
              )}
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
              <span>SpecTree could not read the setup steps from this version of SpecTree Toolbox. Update SpecTree Toolbox.</span>
            </div>
          ) : (
            <>
              <SetupStepList
                bridge={bridge}
                rows={rows}
                azureDevOps={azureDevOps}
                {...(me?.name ? { signedInAs: me.name } : {})}
                onReport={setup.applyReport}
                onRecheck={() => void setup.refetch()}
              />
              {setup.isError ? (
                <p
                  className={cn("mt-2", TOOLBOX_TEXT.content, TOOLBOX_TEXT.faint)}
                  role="status"
                  data-testid="setup-recheck-failed"
                >
                  {SETUP_RECHECK_FAILED}
                </p>
              ) : null}
            </>
          )}
        </div>

        {returnTo && returnName && (queueReturn || noCodeRead) ? (
          <p className={cn("mt-3", TOOLBOX_TEXT.content)} data-testid="setup-return-link">
            <Link className="text-link underline-offset-2 hover:underline" to={returnTo} replace>
              Back to {returnName}
            </Link>
          </p>
        ) : null}

        {noRole ? (
          <AskForRole onCheckAgain={checkAgain} checking={checking} />
        ) : bridge ? (
          <div className="mt-[18px] flex items-center gap-3">
            <span className={cn("flex-1", TOOLBOX_TEXT.content, TOOLBOX_TEXT.faint)}>
              {computerSteps ? SETUP_FOOTER_NOTE : SETUP_FOOTER_NOTE_READ_ONLY}
            </span>
            {checkAgainButton}
            <Button
              className="h-9 bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 md:h-9"
              disabled={!ready}
              onClick={() => void navigate(TOOLBOX_HOME_PATH)}
            >
              Continue
              <ArrowRight aria-hidden="true" />
            </Button>
          </div>
        ) : null}

        {!bridge && computerSteps ? (
          <div className="mt-4">
            <OpenInToolboxCallout reason="Claude Code, Git and your project's code are set up on your computer, so those steps work only inside the SpecTree Toolbox desktop app." />
          </div>
        ) : null}
      </div>
    </div>
  );
}
